import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import UserAvatar from "@/components/common/UserAvatar";
import { Link } from "wouter";
import { ArrowRight, Cake } from "lucide-react";
import { cn } from "@/lib/utils";
import { SectionHelp } from "@/components/help";
import { Badge } from "@/components/ui/badge";
import { useReportTileEmpty } from "./DashboardTile";

interface BirthdayUser {
  id: number;
  name: string;
  photoUrl: string;
  title: string;
  department: string;
  dateOfBirth: string;
  daysUntil: number;
}

export default function TeamBirthdays() {
  const { data: birthdayUsers, isLoading } = useQuery<BirthdayUser[]>({
    queryKey: ["/api/users/upcoming-birthdays"],
  });

  useReportTileEmpty("team_birthdays", !isLoading && (birthdayUsers?.length ?? 0) === 0);

  const users = !isLoading && birthdayUsers ? birthdayUsers.slice(0, 5) : [];

  function getDaysLabel(days: number) {
    if (days === 0) return "Today!";
    if (days === 1) return "Tomorrow";
    return `In ${days} days`;
  }

  function formatBirthdayDate(dateStr: string) {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  return (
    <Card className="h-full border border-border shadow-sm">
      <CardHeader className="pb-4 border-b border-border flex flex-row items-center justify-between">
        <SectionHelp contentId="dashboard:team-members" tooltip="Upcoming team birthdays">
          <CardTitle className="text-lg font-bold text-foreground">Team Birthdays</CardTitle>
        </SectionHelp>
        <Link href="/directory" className="flex items-center gap-1 text-sm font-semibold text-muted-foreground hover:text-foreground">
          View All <ArrowRight className="h-3 w-3" />
        </Link>
      </CardHeader>

      <CardContent className="p-4 space-y-2">
        {isLoading ? (
          <>
            <MemberSkeleton />
            <MemberSkeleton />
            <MemberSkeleton />
          </>
        ) : users.length > 0 ? (
          users.map((user) => (
            <Link
              key={user.id}
              href={`/directory/${user.id}`}
              className={cn(
                "group flex items-center gap-3 p-3 rounded-lg",
                "hover:bg-muted transition-all duration-200",
                user.daysUntil === 0 && "bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800"
              )}
            >
              <UserAvatar name={user.name} src={user.photoUrl} />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-foreground text-sm group-hover:text-foreground transition-colors truncate">
                  {user.name} {user.daysUntil === 0 && "🎂"}
                </h3>
                <p className="text-xs text-muted-foreground truncate">
                  {formatBirthdayDate(user.dateOfBirth)} &middot; {user.title}
                </p>
              </div>
              <Badge
                variant={user.daysUntil === 0 ? "default" : "secondary"}
                className={cn(
                  "text-xs shrink-0",
                  user.daysUntil === 0 && "bg-amber-500 hover:bg-amber-600 text-white"
                )}
              >
                {getDaysLabel(user.daysUntil)}
              </Badge>
            </Link>
          ))
        ) : (
          <div className="py-8 text-center">
            <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center mx-auto mb-3">
              <Cake className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground font-medium text-sm">No upcoming birthdays this week</p>
            <p className="text-muted-foreground text-xs mt-1">Add your birthday in Profile settings</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MemberSkeleton() {
  return (
    <div className="flex items-center gap-3 p-3">
      <Skeleton className="w-10 h-10 rounded-full" />
      <div className="flex-1">
        <Skeleton className="h-4 w-24 mb-1" />
        <Skeleton className="h-3 w-32" />
      </div>
      <Skeleton className="h-5 w-16 rounded" />
    </div>
  );
}
